"use client";

import { Button, Dialog } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { useState, type ReactNode } from "react";
import { StoreEditorNotices } from "./store-editor-notices";
import { StoreEditorPublishButton } from "./store-editor-publish-button";
import { toolbarStyles as styles } from "./toolbar.stylex";

type StoreEditorToolbarProps = Readonly<{
  coworldName: string;
  loaded: boolean;
  loading: boolean;
  dirty: boolean;
  saving: boolean;
  publishing: boolean;
  mutating: boolean;
  canSave: boolean;
  canPublish: boolean;
  awaitingReview: boolean;
  firstPublish: boolean;
  conflict: boolean;
  announcement: "published" | "pending_review" | null;
  failure: Readonly<{ message: string }> | null;
  validationErrors: readonly Readonly<{ field: string; message: string }>[];
  history: ReactNode;
  onSave(): void;
  onPublish(): void;
  onExit(): void;
  onKeepChanges(): void;
  onDiscardChanges(): void;
  onRetry(): void;
}>;

export function StoreEditorToolbar(props: StoreEditorToolbarProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  let status = props.loaded ? "Saved" : "Draft unavailable";

  if (props.dirty) status = "Unsaved changes";

  if (props.saving) status = "Saving…";

  if (props.loading) status = "Loading draft…";

  return (
    <div {...stylex.props(styles.bar)}>
      <div {...stylex.props(styles.row)}>
        <div {...stylex.props(styles.status)}>
          <span {...stylex.props(styles.description)}>
            Editing {props.coworldName}&apos;s store page
          </span>
          <span aria-live="polite" {...stylex.props(styles.muted)}>
            {status}
          </span>
        </div>
        <div {...stylex.props(styles.actions)}>
          <Button
            size="sm"
            disabled={
              props.loading || props.mutating || !props.canSave || !props.dirty
            }
            onClick={props.onSave}
          >
            {props.saving ? "Saving…" : "Save draft"}
          </Button>
          <StoreEditorPublishButton
            disabled={props.loading || props.mutating || !props.canPublish}
            firstPublish={props.firstPublish}
            awaitingReview={props.awaitingReview}
            publishing={props.publishing}
            onPublish={props.onPublish}
          />
          <Dialog
            title="Revision history"
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            trigger={<Button size="sm">History</Button>}
          >
            {props.history}
          </Dialog>
          <Button
            size="sm"
            onClick={props.onExit}
            disabled={props.loading || props.mutating}
          >
            Exit
          </Button>
        </div>
      </div>
      <StoreEditorNotices
        conflict={props.conflict}
        mutating={props.mutating}
        loading={props.loading}
        failure={props.failure}
        validationErrors={props.validationErrors}
        announcement={props.announcement}
        onKeepChanges={props.onKeepChanges}
        onDiscardChanges={props.onDiscardChanges}
        onRetry={props.onRetry}
      />
    </div>
  );
}
